import { Component, Input, OnDestroy, OnInit, input, inject } from '@angular/core';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ReactiveFormsModule
} from '@angular/forms';
import { Structure } from '@app/core/models/structure.model';
import { StructureService } from '@app/shared/structure/structure.service';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { MatFormField, MatLabel, MatInput, MatSuffix, MatError } from '@angular/material/input';
import { MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'app-select-multiple-structures',
    templateUrl: './select-multiple-structures.component.html',
    styleUrls: ['./select-multiple-structures.component.scss'],
    imports: [
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        MatAutocompleteTrigger,
        MatIcon,
        MatSuffix,
        MatTooltip,
        MatError,
        MatAutocomplete,
        MatOption,
        MatButton,
        AsyncPipe
    ]
})
export class SelectMultipleStructuresComponent implements OnInit, OnDestroy {
    private service = inject(StructureService);
    private fb = inject(FormBuilder);

    @Input() name: string;
    readonly label = input<string>();
    @Input() parent: FormGroup;
    disabled = input<boolean>(false);
    readonly structuresSansCodeUaiAutorisees = input<boolean>(false);

    get structuresFormControl(): FormArray {
        return this.parent.get(this.name) as FormArray;
    }
    get lastStructureIsEmpty(): boolean {
        return !!this.structuresFormControl.length && !this.structuresFormControl.controls[this.structuresFormControl.length - 1].value;
    }
    private destroyed$: Subject<void> = new Subject<void>();
    filteredStructures$: Observable<Structure[]>[] = [];
    private readonly obserVableStructures$ = this.service
        .listStructures()
        .pipe(map(structures => structures.filter(s => (s.codeUai != null || this.structuresSansCodeUaiAutorisees()) && s.valid)));

    ngOnInit(): void {
        this.structuresFormControl.value?.forEach((v: string, i: number) => {
            this.initAutoCompleteOnLastField();
            this.structuresFormControl.at(i).addAsyncValidators(this.structureValidator(i));
        });
    }
    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    resetStructure(i: number): void {
        this.structuresFormControl.at(i).reset(null);
    }

    addStructure() {
        this.structuresFormControl.push(
            this.fb.control<string | null>('', { asyncValidators: [this.structureValidator(this.structuresFormControl.length)] })
        );
        this.initAutoCompleteOnLastField();
    }

    initAutoCompleteOnLastField() {
        this.filteredStructures$.push(
            combineLatest([
                this.obserVableStructures$,
                this.structuresFormControl.at(this.structuresFormControl.length - 1).valueChanges.pipe(
                    startWith(''),
                    map((text: string) => {
                        return text ? text?.toUpperCase() : '';
                    })
                )
            ]).pipe(
                takeUntil(this.destroyed$),
                map(([structures, recherche]: [Structure[], string]) => {
                    return structures.filter(s => s.code.startsWith(recherche) || s.libelle.startsWith(recherche));
                })
            )
        );
    }

    getFullValue(structures: Structure[]) {
        return (value: string) => {
            if (value) {
                if (structures.find(st => st.code === value)) {
                    const selectedValue = structures.find(st => st.code === value);
                    return `${selectedValue?.code} - ${selectedValue?.libelle}`;
                } else {
                    return value;
                }
            } else {
                return '';
            }
        };
    }

    getControl(index) {
        return this.structuresFormControl.at(index) as FormControl;
    }

    private structureValidator(index: number): AsyncValidatorFn {
        return (ctrl: AbstractControl): Observable<ValidationErrors | null> => {
            if (!ctrl.value || !ctrl.valueChanges || ctrl.pristine) {
                return of(null);
            }
            return this.filteredStructures$[index]?.pipe(
                take(1),
                switchMap(structures => {
                    if (!structures?.length) {
                        return of({ vide: `Aucune valeur ne correspond.` });
                    } else if (structures.some(v => v.code === ctrl.value)) {
                        return of(null);
                    }
                    return of({ vide: `Aucune valeur ne correspond à ce code.` });
                })
            );
        };
    }
}
